// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import { ActionSheetContext, Body, Button, ButtonGroup, Column, InputText } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { signUp } from '~shared/actions/auth';
import { clearOnboardingVerificationRequired } from '~shared/actions/verifications';
import { VERIFICATION_SENT_BODY } from '~shared/features/onboarding/constants';
import {
  getOnboardingVerificationChannel,
  getOnboardingVerificationContact,
  getOnboardingVerificationUser,
  getOnboardingVerificationUuid,
} from '~shared/features/onboarding/selectors/getOnboardingVerification';
import { getErrorMessage } from '~shared/helpers/helper';
import { mapControllerToInputProps } from '~shared/helpers/mapFieldToInputProps';
import { useCompleteVerification } from '~shared/queries/query-keys/verifications';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { OnboardingMedicalInfoActionSheet } from './OrganicOnboarding';
import { VerificationActionSheet } from './VerificationActionSheet';

export type Values = {
  code: string;
};

export const OnboardingContactVerification = () => {
  const dispatch = useDispatchShared();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { navigate } = useNavigation();
  const user = useSelectorShared(getOnboardingVerificationUser);
  const channel = useSelectorShared(getOnboardingVerificationChannel);
  const uuid = useSelectorShared(getOnboardingVerificationUuid);
  const contact = useSelectorShared(getOnboardingVerificationContact);
  const {
    mutateAsync: completeVerification,
    isPending: completeVerificationLoading,
    error: verificationError,
  } = useCompleteVerification();
  const signupLoading = useSelectorShared((state) => state.ui.loading.signupLoading);
  const loading = completeVerificationLoading || signupLoading;
  const signUpError = useSelectorShared((state) => state.ui.errors.signupError);
  // filter out 409 status signup errors, these will exist by definition of being inside this flow
  const filteredSignUpError = signUpError?.details?.statusCode === 409 ? null : signUpError;
  const error = verificationError || filteredSignUpError;

  const handleResendCode = () => {
    setActiveActionSheet(<VerificationActionSheet resend />);
  };

  const handleBack = () => {
    dispatch(clearOnboardingVerificationRequired());
  };

  const handleVerify = async ({ code }: Values) => {
    let result;
    try {
      result = await completeVerification({ code, comms_channel: channel, uuid });
    } catch {
      // the mutation error renders below the input
      return;
    }
    // eslint-disable-next-line import/no-deprecated
    const { success: signUpSuccess } = await dispatch(signUp({ ...user, ...result }, {}));
    if (signUpSuccess) {
      navigate('RouteAppTabNavigator', { screen: 'RouteTabMyMeds' });
      setActiveActionSheet(<OnboardingMedicalInfoActionSheet />, { dismissible: false });
    }
  };

  const { control, handleSubmit } = useForm({
    defaultValues: {
      code: '',
    },
  });
  const codeController = useController({
    control,
    name: 'code',
    rules: {
      required: 'Verification code cannot be blank',
    },
  });

  return (
    <Column gap="SM">
      <Body>{VERIFICATION_SENT_BODY(channel, contact)}</Body>
      <InputText
        label="Verification code"
        required
        id="code"
        placeholder="XXXXXX"
        textContentType="oneTimeCode"
        {...mapControllerToInputProps(codeController)}
      />
      {!!error && <Body color={COLORS.TEXT_COLORS.DANGER}>{getErrorMessage(error)}</Body>}
      <ButtonGroup
        rowReversed
        buttons={[
          <Button
            key="back-button"
            label="Back"
            type="tertiary"
            onPress={handleBack}
          />,
          <Button
            label="Resend code"
            key="resend-code-button"
            type="secondary"
            onPress={handleResendCode}
          />,
          <Button
            key="verify-button"
            label="Verify"
            loading={loading}
            loadingLabel="Verifying..."
            onPress={handleSubmit(handleVerify)}
          />,
        ]}
      />
    </Column>
  );
};
